import { Box, Button, MenuItem, Popover, TextField } from "@mui/material";
import React, { useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import { endOfDay, PRESET_DAYS, PRESET_LABELS, presetRange, RangePreset, spanDays, startOfDay } from "./reportFormat";

export type DatePreset = RangePreset | "all";

export interface DateRangeValue {
  preset: DatePreset;
  /** Null only for "all". */
  from: Date | null;
  to: Date | null;
}

const LABELS: Record<DatePreset, string> = { all: "All time", ...PRESET_LABELS };

/**
 * The "Dates" box on the Reports page: a few ready ranges and a calendar for
 * any other. `allowAllTime` offers no range at all (the region totals);
 * `maxDays` caps a custom range (the other tabs' server limit).
 */
export const DateRangeField: React.FC<{
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  allowAllTime?: boolean;
  maxDays?: number;
  /** Empty when a label beside the box already names it. */
  label?: string;
  fullWidth?: boolean;
  /** Which ready ranges to offer; all of them when omitted. "Custom" is always offered. */
  presets?: DatePreset[];
}> = ({ value, onChange, allowAllTime = false, maxDays, label = "Dates", fullWidth = false, presets: offered }) => {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const presets = (Object.keys(LABELS) as DatePreset[]).filter(
    (preset) => (allowAllTime || preset !== "all") && (!offered || preset === "custom" || offered.includes(preset)),
  );

  const choose = (preset: DatePreset, anchor: HTMLElement) => {
    if (preset === "custom") {
      setDraft(value.from && value.to ? { from: value.from, to: value.to } : undefined);
      setPickerAnchor(anchor);
      return;
    }
    if (preset === "all") {
      onChange({ preset, from: null, to: null });
      return;
    }
    onChange({ preset, ...presetRange(PRESET_DAYS[preset]) });
  };

  const draftDays = draft?.from && draft?.to ? spanDays(draft.from, draft.to) : 0;
  const draftTooLong = maxDays !== undefined && draftDays > maxDays;

  const apply = () => {
    if (!draft?.from) return;
    onChange({ preset: "custom", from: startOfDay(draft.from), to: endOfDay(draft.to ?? draft.from) });
    setPickerAnchor(null);
  };

  return (
    <>
      <TextField
        select
        label={label || undefined}
        aria-label={label || "Dates"}
        value={value.preset}
        fullWidth={fullWidth}
        sx={{ minWidth: 170 }}
        onChange={(event) => choose(event.target.value as DatePreset, event.target as HTMLElement)}
        SelectProps={{
          renderValue: (preset) =>
            preset === "custom" && value.from && value.to
              ? `${value.from.toLocaleDateString()} – ${value.to.toLocaleDateString()}`
              : LABELS[preset as DatePreset],
        }}
      >
        {presets.map((preset) => (
          <MenuItem
            key={preset}
            value={preset}
            // Picking "Custom" again must reopen the calendar, which a Select
            // does not report as a change.
            onClick={(event) => preset === "custom" && value.preset === "custom" && choose("custom", event.currentTarget)}
          >
            {LABELS[preset]}
          </MenuItem>
        ))}
      </TextField>
      <Popover
        open={!!pickerAnchor}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2 }}>
          <DayPicker mode="range" selected={draft} onSelect={setDraft} disabled={{ after: new Date() }} defaultMonth={draft?.from} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Box sx={{ color: draftTooLong ? "error.main" : "text.secondary", fontSize: 13 }}>
              {draftTooLong ? `At most ${maxDays} days` : draftDays ? `${draftDays} days` : "Pick the first and last day"}
            </Box>
            <Button variant="contained" disabled={!draft?.from || draftTooLong} onClick={apply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
